import { useState } from 'react';
import styled from 'styled-components';
import { useStore } from '../stores/store';
import { installAvailable, triggerInstall, isStandalone, isIOS } from '../lib/pwaInstall';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 12px;
  @media (min-width: 600px) {
    align-items: center;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 18px;
  padding: 22px;
  max-height: 92vh;
  overflow-y: auto;

  h1 { font-size: 1.6rem; margin: 0 0 6px; }
  .sub { color: ${({ theme }) => theme.colors.textMuted}; font-size: 1.05rem; line-height: 1.5; }
  ul { list-style: none; padding: 0; margin: 16px 0; }
  li { padding: 6px 0; font-size: 1.05rem; display: flex; gap: 10px; align-items: center; }
`;

const Note = styled.div`
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border-left: 4px solid ${({ theme }) => theme.colors.gold};
  border-radius: 10px;
  padding: 14px;
  margin: 16px 0;
  font-size: 1.05rem;
  line-height: 1.5;
  strong { color: ${({ theme }) => theme.colors.gold}; }
`;

const InstallCTA = styled.div`
  font-size: 1.05rem;
  line-height: 1.5;
  margin-bottom: 14px;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  width: 100%;
  border: 1.5px solid ${({ theme, $primary }) => ($primary ? theme.colors.accent : theme.colors.borderStrong)};
  background: ${({ theme, $primary }) => ($primary ? theme.colors.accent : 'transparent')};
  color: ${({ theme, $primary }) => ($primary ? theme.colors.accentText : theme.colors.text)};
  border-radius: 12px;
  padding: 13px;
  font-size: 1.05rem;
  font-weight: 700;
  margin-top: 8px;
`;

export function Welcome() {
  const seenWelcome = useStore((s) => s.seenWelcome);
  const setSeenWelcome = useStore((s) => s.setSeenWelcome);
  const [iosHelp, setIosHelp] = useState(false);

  // No mostrar si ya lo vio o si ya está instalada (corriendo como app).
  if (seenWelcome || isStandalone()) return null;

  const handleInstall = async () => {
    if (installAvailable()) {
      const r = await triggerInstall();
      if (r === 'accepted') setSeenWelcome();
      return;
    }
    if (isIOS()) {
      setIosHelp(true);
      return;
    }
    // Navegadores que no exponen el prompt: guía genérica.
    setIosHelp(true);
  };

  return (
    <Overlay>
      <Card>
        <h1>⚽ ¡Bienvenido a VamoVamo!</h1>
        <div className="sub">
          Tu compañero para vivir el <strong>Mundial 2026</strong>. Seguí cada partido y armá tu
          propia historia del torneo.
        </div>

        <ul>
          <li>📅 Calendario y resultados al día</li>
          <li>🏆 Zonas, posiciones y goleadores</li>
          <li>🗝️ Simulá la llave: elegí quién pasa y coroná al campeón</li>
          <li>🏟️ Sedes, estadios y estadísticas</li>
        </ul>

        <InstallCTA>
          📲 <strong>Agregala a tu pantalla de inicio</strong> y tené el Mundial siempre a mano,
          como una app — abre al instante y funciona aún con poca señal.
        </InstallCTA>

        {iosHelp && (
          <Note>
            {isIOS() ? (
              <>📱 En iPhone/iPad: tocá <strong>Compartir</strong> ⬆️ y elegí{' '}
              <strong>"Agregar a inicio"</strong>.</>
            ) : (
              <>💻 Abrí el menú del navegador (⋮) y elegí <strong>"Instalar app"</strong> /{' '}
              <strong>"Agregar a pantalla de inicio"</strong>.</>
            )}
          </Note>
        )}

        <Note>
          ℹ️ Es la <strong>versión gratuita</strong>: la información de cada partido se actualiza
          aproximadamente <strong>2 horas después</strong> de que termina. No es en vivo minuto a
          minuto — los resultados y posiciones llegan con ese pequeño retraso.
        </Note>

        <Btn $primary onClick={handleInstall}>
          📲 Agregar al inicio
        </Btn>
        <Btn onClick={setSeenWelcome}>Entrar a la app</Btn>
      </Card>
    </Overlay>
  );
}
